import DefaultButton from '@/components/common/default/DefaultButton';
import { CommonEnum } from '@/types/translations/CommonEnum';
import { Check } from 'lucide-react';
import { memo } from 'react';

import { useToggleRefinement } from 'react-instantsearch';
import useTypedTranslation from '@/hooks/useTypedTranslation';

interface ToggleFilterProps {
  attribute: string;
  label: string;
}

const ToggleFilter = memo<ToggleFilterProps>(({ attribute, label }) => {
  const { translate } = useTypedTranslation<CommonEnum>('common');
  const {
    value: currentValue,
    refine,
    canRefine,
  } = useToggleRefinement({
    attribute,
    // For boolean attributes, use true as 'on' value
    // Don't specify 'off' - let it be the absence of filter
    on: true,
  });

  return (
    <DefaultButton
      type="button"
      onClick={() => {
        if (canRefine) {
          const newValue = currentValue.isRefined;
          refine({ isRefined: newValue });
        }
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          if (canRefine) {
            const newValue = currentValue.isRefined;
            refine({ isRefined: newValue });
          }
        }
      }}
      disabled={!canRefine}
      className={`tw-group tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-px-2 tw-py-1.5 tw-text-left tw-text-sm tw-transition-all tw-duration-200 hover:tw-bg-gray-50 focus:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-gray-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-50 ${
        currentValue.isRefined ? 'tw-bg-blue-50 tw-text-blue-900' : 'tw-text-gray-700'
      }`}
      aria-pressed={currentValue.isRefined}
      aria-label={`${currentValue.isRefined ? translate(CommonEnum.disable_variableName_filter).replace('{{variableName}}', label) : translate(CommonEnum.enable_variableName_filter).replace('{{variableName}}', label)}`}
    >
      <div className="tw-flex tw-items-center tw-gap-2">
        <div
          className={`tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded tw-border-2 !tw-border-solid tw-transition-colors ${
            currentValue.isRefined
              ? 'tw-border-blue-500 tw-bg-blue-500 tw-text-white'
              : 'tw-border-gray-300 tw-bg-white group-hover:tw-border-gray-400'
          }`}
        >
          {currentValue.isRefined && <Check className="tw-h-3 tw-w-3" />}
        </div>
        <span className="tw-flex-1">{label}</span>
      </div>
    </DefaultButton>
  );
});

ToggleFilter.displayName = 'ToggleFilter';

export default ToggleFilter;
